import { customerInfoProviderToken } from "@/tokens";
import { REWARDS_CUSTOMER_ELIGIBILITY_TYPES } from "@apps/rewards/constants/rewardsCustomerEligibilityTypes";
import RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
import { PROGRAM_STATUS } from "@apps/rewards/modules/StorefrontValues/constants";
import type { RewardsStorefrontValuesCustomersEligibilitySettingsEntity } from "@apps/rewards/modules/StorefrontValues/models/RewardsStorefrontValuesEntities";
import type { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { ROLE } from "@modules/authentication/constants";
import type { Observable } from "rxjs";
import { combineLatest, map, shareReplay } from "rxjs";
import { inject, singleton } from "tsyringe";

import RewardsStorefrontValuesService from "../../modules/StorefrontValues/service/RewardsStorefrontValuesService";

@singleton()
class RewardsCustomerEligibilityService {
    private readonly customerTagsList: string[];
    private readonly isCustomer$: Observable<boolean>;

    constructor(
        private readonly rewardsStorefrontValuesService: RewardsStorefrontValuesService,
        private readonly rewardsProgramAccountQuery: RewardsProgramAccountQuery,
        private readonly authenticationModule: AuthenticationModule,
        @inject(customerInfoProviderToken)
        private readonly customerInfoProvider: CustomerInfoProviderInterface
    ) {
        this.customerTagsList = this.customerInfoProvider.getTags();
        this.isCustomer$ =
            this.authenticationModule.exports.authenticationInfoProvider
                .selectRole()
                .pipe(map((role) => role === ROLE.Customer));
    }

    private canUseRewardsProgram(
        type: REWARDS_CUSTOMER_ELIGIBILITY_TYPES,
        settingsTagList: string[],
        customerTagList: string[]
    ) {
        const hasMatchingTags = customerTagList.some((tag) =>
            settingsTagList.includes(tag)
        );

        if (type === REWARDS_CUSTOMER_ELIGIBILITY_TYPES.include) {
            return hasMatchingTags;
        } else {
            return !hasMatchingTags;
        }
    }

    private isEligibleCustomer(
        {
            isEnabled,
            type,
            tags,
            showWidgetsToGuests,
        }: RewardsStorefrontValuesCustomersEligibilitySettingsEntity,
        isCustomer: boolean
    ): boolean {
        if (!isEnabled) return true;

        if (!isCustomer) return showWidgetsToGuests;

        return this.canUseRewardsProgram(type, tags, this.customerTagsList);
    }

    public selectIsEligibleCustomer(): Observable<boolean> {
        return combineLatest([
            this.rewardsStorefrontValuesService.selectRewardsCustomersEligibilitySettings(),
            this.isCustomer$,
        ]).pipe(
            map(([eligibilitySettings, isCustomer]) =>
                this.isEligibleCustomer(eligibilitySettings, isCustomer)
            ),
            shareReplay({
                bufferSize: 1,
                refCount: true,
            })
        );
    }

    private canUseReferralProgram = ({
        isReferralProgramEnabled,
        isCustomer,
        canUseReferralProgram,
    }: {
        isReferralProgramEnabled: boolean;
        isCustomer: boolean;
        canUseReferralProgram: boolean;
    }): boolean =>
        isReferralProgramEnabled && (!isCustomer || canUseReferralProgram);

    public selectCanUseReferralProgram(): Observable<boolean> {
        return combineLatest([
            this.rewardsStorefrontValuesService.selectRewardsProgramStatus(
                PROGRAM_STATUS.referralProgramEnabled
            ),
            this.isCustomer$,
            this.rewardsProgramAccountQuery.referralData$,
        ]).pipe(
            map(([isReferralProgramEnabled, isCustomer, referralData]) =>
                this.canUseReferralProgram({
                    isReferralProgramEnabled,
                    isCustomer,
                    canUseReferralProgram:
                        referralData?.isReferralProgramAvailableForCustomer ??
                        false,
                })
            )
        );
    }
}

export default RewardsCustomerEligibilityService;
